/* One screen, the Clicky way: look at the page in front of the owner when
   they ask, and only that page. The Zana panel is not inside it. The model
   is told the cards and the plates actually showing. When the answer names
   one of them, the same ring the walkthrough uses lands on it.

   Plates are stored without spaces, upper case, because the server matches
   a bus by that id and drops anything it does not already know. */

export interface ScreenCard { label: string; value: string }
export interface ScreenMark { key: string; label: string }

export interface ScreenLook {
  cards: ScreenCard[];
  /** Plates visible in the page, in reading order. */
  plates: string[];
  /** Plate of the bus sheet that is open, when there is one. */
  selected?: string;
}

const PLATE = /\b([A-Z]{3}\s?\d{3}[A-Z])\b/g;

export function plateId(plate: string): string {
  return String(plate || '').toUpperCase().replace(/[\s-]/g, '');
}

/** Every Kenyan plate in this text, once, in order. */
export function platesIn(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const re = new RegExp(PLATE.source, 'g');
  let m: RegExpExecArray | null;
  const up = String(text || '').toUpperCase();
  while ((m = re.exec(up))) {
    const id = plateId(m[1]);
    if (id && !seen.has(id)) { seen.add(id); out.push(id); }
  }
  return out;
}

function reading(el: Element, max = 80): string {
  if (el.tagName === 'TR') {
    const cells = Array.from(el.querySelectorAll('td, th'))
      .map((c) => (c.textContent || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    if (cells.length) return cells.join(' ').slice(0, max);
  }
  const raw = (el as HTMLElement).innerText || el.textContent || '';
  return raw.replace(/\s+/g, ' ').trim().slice(0, max);
}

function oneLine(el: Element): string {
  return reading(el, 80);
}

export interface ScreenPick {
  el: HTMLElement;
  /** Short name for the pill. */
  label: string;
  /** The question, carrying what that element actually says. */
  question: string;
}

/** The row or card under a double-click, and a question that quotes it.
    A bare cell ("Idle", "0") is not enough: she is handed the whole row
    and told to use the rest of the page. */
export function describePick(target: HTMLElement, root: HTMLElement, points: ScreenMark[] = []): ScreenPick | null {
  if (!root.contains(target)) return null;
  const marked = target.closest('[data-zx], [data-z], tr') as HTMLElement | null;
  let el = marked && root.contains(marked) ? marked : target;
  if (el === target) {
    let parent = target.parentElement;
    while (parent && parent !== root && reading(parent, 400).length <= 240) {
      el = parent;
      parent = parent.parentElement;
    }
  }
  const text = reading(el, 280);
  if (text.length < 2) return null;
  const key = el.getAttribute('data-zx') || '';
  const point = points.find((p) => p.key === key);
  const plate = platesIn(text)[0];
  const label = (point?.label || plate || text).slice(0, 42);
  const question = `Explain what I selected on this page: ${label}. It reads: "${text}". Use the rest of this page. Say what this is, what the figures mean, and what I should do about it.`;
  return { el, label, question };
}

/** What the main column is showing right now. `root` is the page body, not the panel. */
export function lookAtMain(root: ParentNode | null, points: ScreenMark[] = []): ScreenLook {
  if (!root) return { cards: [], plates: [] };
  const names = new Map(points.map((p) => [p.key, p.label]));
  const cards: ScreenCard[] = [];
  root.querySelectorAll('[data-zx]').forEach((el) => {
    if (el.parentElement && el.parentElement.closest('[data-zx]')) return;
    const key = el.getAttribute('data-zx') || '';
    const label = (names.get(key) || el.getAttribute('aria-label') || '').trim();
    const value = oneLine(el);
    if (!label || !value || cards.length >= 6) return;
    cards.push({ label: label.slice(0, 48), value });
  });
  const sheet = root.querySelector('[data-z^="sheet:"]');
  const selected = sheet ? platesIn(sheet.textContent || '')[0] : undefined;
  return { cards, plates: platesIn(root.textContent || '').slice(0, 12), selected };
}

export interface PageScreen {
  cards?: ScreenCard[];
  focus?: string[];
  entityId?: string;
  window?: string;
}

/** The page's own figures first, then whatever the look just read, without repeating a label. */
export function mergeScreen(page: PageScreen | undefined, look: ScreenLook): { cards: ScreenCard[]; focus: string[]; entityId?: string; window?: string; visible: string[] } {
  const cards = [...(page?.cards || [])];
  look.cards.forEach((c) => {
    if (cards.length >= 6) return;
    if (cards.some((x) => x.label.toLowerCase() === c.label.toLowerCase())) return;
    cards.push(c);
  });
  const focus: string[] = [];
  const seen = new Set<string>();
  [...(page?.focus || []), ...look.plates].forEach((id) => {
    const p = plateId(id);
    if (!p || seen.has(p) || focus.length >= 12) return;
    seen.add(p);
    focus.push(p);
  });
  return {
    cards,
    focus,
    entityId: page?.entityId || look.selected,
    window: page?.window,
    visible: look.plates.slice(0, 12),
  };
}

function smallestWithPlate(root: ParentNode, plate: string): HTMLElement | null {
  const want = plateId(plate);
  let best: HTMLElement | null = null;
  let bestLen = Infinity;
  root.querySelectorAll('[data-z], [data-zx], tr').forEach((node) => {
    const el = node as HTMLElement;
    const text = plateId(el.textContent || '');
    if (!text.includes(want)) return;
    const len = (el.textContent || '').length;
    if (len < bestLen) { best = el; bestLen = len; }
  });
  return best;
}

/** The element the answer is talking about, or null when it names nothing on this screen. */
export function pointOnScreen(answer: string, root: ParentNode | null, points: ScreenMark[] = []): { el: HTMLElement; label: string } | null {
  if (!root) return null;
  const named = platesIn(answer);
  for (const plate of named) {
    const el = smallestWithPlate(root, plate);
    if (el) return { el, label: plate };
  }
  const hits = points
    .filter((p) => p.label.length > 3 && answer.toLowerCase().includes(p.label.toLowerCase()))
    .sort((a, b) => b.label.length - a.label.length);
  for (const p of hits) {
    const el = Array.from(root.querySelectorAll('[data-zx]')).find((n) => n.getAttribute('data-zx') === p.key) as HTMLElement | undefined;
    if (el) return { el, label: p.label };
  }
  return null;
}
